<template>
  <Card class="richtext-button">
    <template #header>
      <h1>{{ $t('createButton') }}</h1>
      <p class="richtext-button__subtitle">{{ $t('customizeButtonDisplayMessage') }}</p>
    </template>

    <template #default>
      <div class="richtext-button__form">
        <div class="form-row">
          <div class="form-col form-col--grow">
            <InputComponent
              v-model="form.label"
              :label="$t('buttonLabel')"
              :placeholder="$t('buttonTextPlaceholder')"
            />
          </div>
          <div class="form-col form-col--fixed-small" v-if="enableIcon">
            <InputComponent :label="$t('chooseIcon')">
              <Webcomponent
                v-model="form.icon"
                tag="kvass-icon-selector"
                default-search=""
                source="https://unpkg.com/@kvass/widgets@latest/dist/icon-selector.js"
              />
            </InputComponent>
          </div>
        </div>

        <div v-if="internalItems" class="form-section">
          <label class="elder-input__label">{{ $t('buttonDestinationText') }}</label>
          <ButtonGroup>
            <ButtonComponent
              v-if="internalItems"
              :label="$t('internalPage')"
              :class="{ active: form.type === 'internal' }"
              @click="form.type = 'internal'"
            />
            <ButtonComponent
              :label="$t('externalPage')"
              :class="{ active: form.type === 'external' }"
              @click="form.type = 'external'"
            />
          </ButtonGroup>
        </div>

        <div class="form-section">
          <div class="anchor-checkbox" v-if="form.type === 'internal'">
            <CheckboxComponent v-model="useAnchor" :label="$t('useAnchorLink')" />
          </div>
          <div v-if="form.type === 'internal'" class="form-row">
            <div class="form-col" v-if="!useAnchor">
              <div v-if="internalItems">
                <InputComponent :label="$t('chooseSubpage')" class="subpage-select">
                  <SelectComponent
                    v-model="selectedPageObj"
                    :items="fetchPages"
                    search
                    :title="$t('chooseSubpage')"
                    :modal-wrapper="'div'"
                    :compare="(a, b) => a.value === b.value"
                  >
                    <template #item="{ item }">
                      {{ item.label }}
                    </template>

                    <template #empty>
                      <div>
                        {{ $t('choosePage') }}
                      </div>
                    </template>
                  </SelectComponent>
                </InputComponent>
              </div>
            </div>

            <div class="form-col" v-if="useAnchor">
              <InputComponent :label="$t('chooseAnchorLink')">
                <select v-model="selectedAnchor" class="elder-input__element">
                  <option value="" selected>{{ $t('none') }}...</option>

                  <option v-for="anchor in anchorItems" :key="anchor.value" :value="anchor.value">
                    {{ anchor.label }}
                  </option>

                  <option value="custom">{{ $t('custom') }}...</option>
                </select>
              </InputComponent>

              <div v-if="selectedAnchor === 'custom'" class="custom-achor-input">
                <InputComponent
                  v-model="customAnchorText"
                  :placeholder="$t('customAnchorIdPlaceholder')"
                  :label="$t('customAnchorLabel')"
                />
              </div>
            </div>
          </div>

          <div v-else>
            <InputComponent v-model="form.externalUrl" :label="$t('url')" placeholder="https://..." />
          </div>
        </div>

        <div class="form-section">
          <label class="elder-input__label">{{ $t('theme') }}</label>
          <ButtonGroup>
            <ButtonComponent
              v-for="theme in availableThemes"
              :key="theme.value"
              :label="$t(theme.label)"
              :class="{ active: form.theme === theme.value }"
              @click="form.theme = theme.value"
            >
              <template #label>
                <span class="richtext-button__theme-button-label">
                  <span
                    v-if="theme.style"
                    class="richtext-button__theme-button-label-color-indicator"
                    :style="`${theme.style}`"
                  ></span>
                  <span> {{ $t(theme.label) }}</span>
                </span>
              </template></ButtonComponent
            >
          </ButtonGroup>
          <div class="richtext-button__preview">
            <label class="mb-1">{{ $t('previewMode') }}</label>
            <ButtonComponent
              :label="form.label || '----'"
              :icon="form.icon"
              :theme="form.theme"
              :style="selectedThemeStyle"
              @click.prevent
            />
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="richtext-button__actions">
        <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="$emit('close')" />
        <ButtonComponent :label="$t('save')" theme="primary" :icon="['fal', 'arrow-to-bottom']" @click="save" />
      </div>
    </template>
  </Card>
</template>

<script>
import Card from '@/components/Card-v2.vue'
import { InputComponent } from 'vue-elder-input'
import { ButtonComponent } from 'vue-elder-button'
import { CheckboxComponent } from 'vue-elder-checkbox'
import SelectComponent from '@/components/Select/Modal'
import Webcomponent from '@kvass/custom-fields/src/Fields/Webcomponent'
import ButtonGroup from '@kvass/button-group'

export default {
  props: {
    initialData: Object,
    onSave: Function,
    internalItems: {
      type: String,
      default: '',
    },
    pages: {
      type: Array,
      default: () => [],
    },
    anchorItems: {
      type: Array,
      default: () => [],
    },
    colors: {
      type: Array,
      default: () => [],
    },
    getThemes: {
      type: Function,
      default: () => [],
    },
    enableIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      subpages: [],
      selectedAnchor: '',
      customAnchorText: '',
      useAnchor: false,
      form: {
        label: '',
        icon: '',
        type: 'internal',
        externalUrl: '',
        internalPage: '',
        internalAnchor: '',
        theme: 'primary',
        ...this.initialData,
      },
    }
  },
  computed: {
    selectedPageObj: {
      get() {
        if (!this.form.internalPage) return
        return [...this.pages, ...this.subpages].find(p => p.value === this.form.internalPage)
      },
      set(val) {
        this.form.internalPage = val ? val.value : ''
      },
    },
    selectedTheme() {
      return this.availableThemes.find(t => t.value === this.form.theme)
    },
    selectedThemeStyle() {
      return this.selectedTheme ? this.selectedTheme.style : null
    },
    availableThemes() {
      let themes = [
        { value: 'primary', label: 'primaryColor' },
        { value: 'secondary', label: 'secondaryColor' },
      ]

      const themesFromTemplate = typeof this.getThemes === 'function' ? this.getThemes() : []

      if (themesFromTemplate.length) {
        themes = []
        themesFromTemplate.forEach(theme => {
          themes.push(theme)
        })
      }

      if (!this.colors || this.colors.length === 0) {
        return themes
      }

      return themes.filter(t => this.colors.includes(t.value))
    },
  },
  mounted() {
    if (this.availableThemes.length > 0) {
      const isCurrentValid = this.availableThemes.some(t => t.value === this.form.theme)
      if (!isCurrentValid) {
        this.form.theme = this.availableThemes[0].value
      }
    }

    if (!this.internalItems) {
      this.form.type = 'external'
      if (!this.form.externalUrl && this.form.href) {
        this.form.externalUrl = this.form.href
      }
    }

    this.initAnchorState()
    if (this.internalItems) {
      this.fetchInternalItems().then(subpages => (this.subpages = subpages))
    }
  },
  methods: {
    async fetchPages({ search }) {
      const subpages = await this.fetchInternalItems(search)
      return [...this.pages.filter(p => p.label.toLowerCase().includes((search || '').toLowerCase())), ...subpages]
    },
    fetchInternalItems(search) {
      const url = search ? `${this.internalItems}&search=${encodeURIComponent(search)}` : this.internalItems
      return fetch(url)
        .then(response => response.json())
        .catch(() => [])
    },
    initAnchorState() {
      const current = this.form.internalAnchor

      if (!current || current === '#top') {
        this.selectedAnchor = ''
        this.useAnchor = false
        return
      }

      if (this.pages.some(p => p.value === current)) {
        this.form.internalPage = current
        return
      }

      this.useAnchor = true

      const isPredefined = this.anchorItems.some(a => a.value === current)

      if (isPredefined) {
        this.selectedAnchor = current
      } else {
        this.selectedAnchor = 'custom'
        this.customAnchorText = current
      }
    },

    save() {
      let href = ''

      if (this.form.type === 'external') {
        href = this.form.externalUrl
      } else {
        if (this.useAnchor) {
          let anchor = this.selectedAnchor === 'custom' ? this.customAnchorText : this.selectedAnchor || ''

          if (anchor) {
            if (!anchor.startsWith('#')) {
              anchor = `#${anchor}`
            }
            href = anchor
          }
        } else {
          href = this.form.internalPage
        }
      }

      const finalData = {
        label: this.form.label,
        href: href,
        theme: this.form.theme,
        target: this.form.type === 'external' ? '_blank' : '_self',
        icon: this.form.icon,
      }

      if (this.onSave) {
        this.onSave(finalData)
      }

      this.$emit('close')
    },
  },
  components: {
    Card,
    InputComponent,
    ButtonComponent,
    CheckboxComponent,
    SelectComponent,
    Webcomponent,
    ButtonGroup,
  },
}
</script>

<style lang="scss">
.richtext-button {
  width: 50rem;

  &__preview {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: 0.5rem;
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    background-color: #f7f7f7;

    // preview only – not clickable
    .elder-button {
      pointer-events: none;
    }
  }
  &__theme-button-label {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    &-color-indicator {
      box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12), 0 6px 12px rgba(15, 23, 42, 0.1);

      border: 1px solid rgba(15, 23, 42, 0.08);
      $size: 20px;
      height: $size;
      width: $size;
      border-radius: 50%;
    }
  }
  .richtext-button__subtitle {
    margin-top: 0.5rem;
    font-size: 0.9em;
    color: #666;
    font-weight: normal;
  }

  button {
    &.active {
      background-color: #d3e6f7;
    }
  }

  .richtext-button__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-row {
    display: flex;
    gap: 1.5rem;
    align-items: stretch;
  }

  .select-modal__input {
    div {
      padding: 1.1em;
    }
  }

  .form-col {
    flex: 1;
    min-width: 0;

    &--grow {
      flex: 2;
    }

    &--fixed-small {
      flex: 0 0 140px;
    }
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .richtext-button {
    &__actions {
      display: flex;
      gap: 1rem;
    }
  }

  .anchor-checkbox {
    margin-bottom: 1rem;
  }
}

.elder-modal:has(.richtext-button) {
  z-index: get-layer('modal') + 1 !important;
}
</style>
